import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { SERVER_URL, AUTH_STORAGE_KEY } from "../socket";
import brandLogo from "../TimeEdgeIcon.png";
import "../styles.css";
import Header from "./Header";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";

const CORPORATE_DOMAIN = "@krivaone.com";

const isAtLeast12YearsOld = (dob) => {
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
  return age >= 12;
};

const twoColStyles = `
  .form-row {
    display: flex;
    gap: 16px;
  }
  .form-row .form-col {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  @media (max-width: 640px) {
    .form-row {
      flex-direction: column;
      gap: 0;
    }
  }
`;

function RegisterPage() {
  const [loading, setLoading]             = useState(false);
  const [message, setMessage]             = useState("");
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [success, setSuccess]             = useState(false);

  // country auto-detection
  const [detectingCountry, setDetectingCountry] = useState(true);

  // password visibility
  const [showPassword, setShowPassword]               = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToPolicy, setAgreedToPolicy]           = useState(false);

  const [form, setForm] = useState({
    firstName:     "",
    lastName:      "",
    dateOfBirth:   "",
    gender:        "",
    country:       "",
    emailPrefix:   "",
    password:      "",
    confirmPassword: "",
  });

  const email = useMemo(
    () =>
      form.emailPrefix
        ? `${form.emailPrefix.trim().toLowerCase()}${CORPORATE_DOMAIN}`
        : "",
    [form.emailPrefix]
  );

  // ── Auto-detect country on mount ─────────────────────────────────────────
  useEffect(() => {
    const detectCountry = async () => {
      try {
        setDetectingCountry(true);
        const res = await axios.get("https://ipapi.co/json/");
        const detected = res.data?.country_name;
        if (detected) {
          setForm((prev) => ({ ...prev, country: detected }));
        }
      } catch {
        // fall back silently — user can be prompted to retry if needed
      } finally {
        setDetectingCountry(false);
      }
    };
    detectCountry();
  }, []);

  // ── Email availability check ─────────────────────────────────────────────
  useEffect(() => {
    if (!form.emailPrefix || form.emailPrefix.length < 7) {
      setEmailAvailable(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setCheckingEmail(true);
        const res = await axios.post(`${SERVER_URL}/api/auth/check-email`, { email });
        setEmailAvailable(Boolean(res.data?.available));
      } catch {
        setEmailAvailable(false);
      } finally {
        setCheckingEmail(false);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [form.emailPrefix, email]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMessage("");
    if (name === "emailPrefix") {
      const cleaned = value
        .replace(/@.*$/, "")
        .replace(/[^a-zA-Z0-9._-]/g, "")
        .toLowerCase();
      setForm({ ...form, emailPrefix: cleaned });
      return;
    }
    setForm({ ...form, [name]: value });
  };

  const validateForm = () => {
    if (!form.firstName?.trim()) { setMessage("First name is required"); return false; }
    if (!form.lastName?.trim())  { setMessage("Last name is required");  return false; }

    if (!form.dateOfBirth) { setMessage("Date of birth is required"); return false; }
    if (!isAtLeast12YearsOld(form.dateOfBirth)) { setMessage("You must be at least 12 years old"); return false; }
    if (!form.gender)      { setMessage("Select gender");             return false; }
    if (!form.country)     { setMessage("Country could not be detected — please retry"); return false; }

    if (!form.emailPrefix)             { setMessage("Email ID is required");                        return false; }
    if (form.emailPrefix.length < 7)   { setMessage("Email user id must be at least 7 characters"); return false; }
    if (checkingEmail)                  { setMessage("Checking email availability...");              return false; }
    if (emailAvailable === false)       { setMessage("Email already exists");                        return false; }

    if (form.password.length < 6)              { setMessage("Password must be at least 6 characters"); return false; }
    if (form.password !== form.confirmPassword) { setMessage("Passwords do not match");                return false; }
    if (!agreedToPolicy)                        { setMessage("Please accept the Privacy Policy to continue"); return false; }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!validateForm()) return;

    try {
      setLoading(true);
      const res = await axios.post(`${SERVER_URL}/api/auth/register`, {
        email,
        password:    form.password,
        fullName:    `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
        dateOfBirth: form.dateOfBirth,
        gender:      form.gender,
        country:     form.country,
      });

      setSuccess(true);

      const { token, user } = res.data;
      if (token && user) {
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(res.data));
        window.location.href = "/";
      } else {
        window.location.href = "/login";
      }
    } catch (err) {
      setMessage(err.response?.data?.error || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{twoColStyles}</style>
      <Header />

      <div className="login-page">
        <div className="login-layout login-layout--single">

          <div className="login-card">
            <center>
              <img src={brandLogo} className="imglogo" alt="Brand Logo" />
            </center>

            <h2 className="login-title">Create Account</h2>

            {/* Success box */}
            {success && (
              <div className="success-box">
                <h3>Registration Successful</h3>
                <p>Your account has been created successfully.</p>
                <p style={{ fontSize: 13, color: "#777" }}>
                  Redirecting to login…
                </p>
                <button
                  className="btn-primary"
                  style={{ marginTop: 12 }}
                  onClick={() => (window.location.href = "/login")}
                >
                  Go to Login
                </button>
              </div>
            )}

            {message && <div className="login-error">{message}</div>}

            {!success && (
              <form onSubmit={handleSubmit} className="login-form">

                {/* Row 1: First Name / Last Name */}
                <div className="form-row">
                  <div className="form-col">
                    <label>First Name</label>
                    <input
                      name="firstName"
                      value={form.firstName}
                      onChange={handleChange}
                      placeholder="Enter first name"
                    />
                  </div>
                  <div className="form-col">
                    <label>Last Name</label>
                    <input
                      name="lastName"
                      value={form.lastName}
                      onChange={handleChange}
                      placeholder="Enter last name"
                    />
                  </div>
                </div>

                {/* Row 2: Date of Birth / Gender */}
                <div className="form-row">
                  <div className="form-col">
                    <label>Date of Birth</label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={form.dateOfBirth}
                      onChange={handleChange}
                      max={new Date(
                        new Date().setFullYear(new Date().getFullYear() - 12)
                      ).toISOString().split("T")[0]}
                    />
                  </div>
                  <div className="form-col">
                    <label>Gender</label>
                    <select name="gender" value={form.gender} onChange={handleChange}>
                      <option value="">Select gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                {/* Row 3: Country / Email */}
                <div className="form-row">
                  <div className="form-col">
                    <label>Country</label>
                    <input
                      name="country"
                      value={
                        detectingCountry
                          ? "Detecting your country…"
                          : form.country || "Unable to detect — check connection"
                      }
                      readOnly
                      disabled
                      style={{ backgroundColor: "#f1f5f9", color: "#475569" }}
                    />
                  </div>
                  <div className="form-col">
                    <label>Email Address</label>
                    <input
                      name="emailPrefix"
                      value={form.emailPrefix}
                      onChange={handleChange}
                      placeholder={`username${CORPORATE_DOMAIN}`}
                      autoCapitalize="none"
                    />
                    {form.emailPrefix && form.emailPrefix.length < 7 && (
                      <small style={{ color: "red" }}>
                        Email user id must be at least 7 characters
                      </small>
                    )}
                    {checkingEmail && (
                      <small style={{ color: "#888" }}>Checking availability…</small>
                    )}
                    {emailAvailable === true && (
                      <small style={{ color: "green" }}>✔ Email is available</small>
                    )}
                    {emailAvailable === false && (
                      <small style={{ color: "red" }}>✘ Email already exists</small>
                    )}
                  </div>
                </div>

                {/* Row 4: Password / Confirm Password */}
                <div className="form-row">
                  <div className="form-col">
                    <label>Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={form.password}
                        onChange={handleChange}
                        placeholder="••••••••"
                      />
                      <span
                        className="password-toggle"
                        onClick={() => setShowPassword((prev) => !prev)}
                      >
                        {showPassword
                          ? <AiOutlineEyeInvisible size={18} />
                          : <AiOutlineEye size={18} />}
                      </span>
                    </div>
                  </div>
                  <div className="form-col">
                    <label>Confirm Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={form.confirmPassword}
                        onChange={handleChange}
                        placeholder="••••••••"
                      />
                      <span
                        className="password-toggle"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                      >
                        {showConfirmPassword
                          ? <AiOutlineEyeInvisible size={18} />
                          : <AiOutlineEye size={18} />}
                      </span>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 13,
                    color: "#475569",
                  }}
                >
                  <input
                    id="privacy-policy-checkbox"
                    type="checkbox"
                    style={{ marginTop: 20, width: 20 }}
                    checked={agreedToPolicy}
                    onChange={(e) => setAgreedToPolicy(e.target.checked)}
                  />

                  <label htmlFor="privacy-policy-checkbox" style={{ margin: 0 }}>
                    I agree to the{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        color: "#2563eb",
                        textDecoration: "none",
                        fontWeight: 500,
                      }}
                    >
                      Privacy Policy
                    </a>
                  </label>
                </div>

                <div className="step-buttons">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={loading || checkingEmail}
                  >
                    {loading ? "Please wait..." : "Register"}
                  </button>
                </div>
              </form>
            )}

            <div className="login-footer">
              <a href="/login">Already have an account? Login</a>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}

export default RegisterPage;
